<template>
  <div class="modal-overlay">
    <div class="modal-content-small">
      <div class="modal-header">
        <div class="title-container">
          <h4 class="title">Edit Holiday</h4>
        </div>
        <div class="modal-header-buttons">
          <button class="close-button" @click="closeModal">
            <i class="fa fa-times"></i>
          </button>
        </div>
      </div>
      <div class="modal-body p-4">
        <div class="form-group mb-3">
          <label class="form-label">Select Date</label>
          <input type="date" class="form-control" v-model="form.date" />
        </div>
        <div class="form-group mb-3">
          <label class="form-label">Holiday Name</label>
          <input type="text" class="form-control" v-model="form.name" placeholder="Enter holiday name" />
        </div>
        <div class="form-group mb-4">
          <label class="form-label d-block mb-2">Holiday Type</label>
          <div class="row g-2">
            <div class="col-6">
              <label class="d-flex align-items-center gap-2 m-0 cursor-pointer">
                <input type="checkbox" value="public" v-model="form.types" /> Public
              </label>
            </div>
            <div class="col-6">
              <label class="d-flex align-items-center gap-2 m-0 cursor-pointer">
                <input type="checkbox" value="bank" v-model="form.types" /> Bank
              </label>
            </div>
            <div class="col-6">
              <label class="d-flex align-items-center gap-2 m-0 cursor-pointer">
                <input type="checkbox" value="mercantile" v-model="form.types" /> Mercantile
              </label>
            </div>
            <div class="col-6">
              <label class="d-flex align-items-center gap-2 m-0 cursor-pointer">
                <input type="checkbox" value="poya" v-model="form.types" /> Poya
              </label>
            </div>
          </div>
        </div>
        
        <div class="form-group mb-4 d-flex align-items-center justify-content-between border-top pt-3">
          <label class="form-label mb-0">Is Active</label>
          <label class="toggle-switch">
            <input type="checkbox" v-model="form.isActive" />
            <span class="slider round"></span>
          </label>
        </div>

        <div class="d-flex justify-content-end gap-2 mt-2">
          <button class="btn btn-secondary" @click="closeModal" :disabled="isSaving">Cancel</button>
          <button class="btn btn-primary" @click="updateHoliday" :disabled="isSaving">
            <span v-if="isSaving"><i class="fa fa-spinner fa-spin me-1"></i>Updating...</span>
            <span v-else>Update</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useToast } from "vue-toastification";
import { updateHolidayData } from "../../services/dataService";

export default {
  name: 'EditHolidayModal',
  props: {
    holidayData: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      isSaving: false,
      form: {
        date: '',
        name: '',
        types: [],
        isActive: true
      }
    };
  },
  mounted() {
    this.populateForm();
    document.body.style.overflow = 'hidden';
  },
  beforeDestroy() {
    document.body.style.overflow = 'auto';
  },
  watch: {
    holidayData: {
      deep: true,
      handler() {
        this.populateForm();
      }
    }
  },
  methods: {
    populateForm() {
      if (this.holidayData) {
        this.form = {
          date: this.holidayData.date || '', // assuming date is in YYYY-MM-DD
          name: this.holidayData.name || '',
          types: [...(this.holidayData.types || [])],
          isActive: this.holidayData.is_active !== undefined ? this.holidayData.is_active : true
        };
      }
    },
    closeModal() {
      this.$emit('close');
    },
    async updateHoliday() {
      const toast = useToast();

      // Front-end validations
      if (!this.form.date) {
        return toast.error("Please select a date.", { position: "top-right", timeout: 3000 });
      }
      if (!this.form.name || !this.form.name.trim()) {
        return toast.error("Please enter a holiday name.", { position: "top-right", timeout: 3000 });
      }
      if (this.form.types.length === 0) {
        return toast.error("Please select at least one holiday type.", { position: "top-right", timeout: 3000 });
      }

      this.isSaving = true;

      let year = null, month = null, day = null;
      if (this.form.date) {
        const parts = this.form.date.split('-');
        if (parts.length === 3) {
          year = parseInt(parts[0], 10);
          month = parseInt(parts[1], 10);
          day = parseInt(parts[2], 10);
        }
      }

      const profileId = parseInt(localStorage.getItem('profile_id'));

      const payload = {
        year: year,
        month: month,
        day: day,
        holiday: this.form.name,
        is_public: this.form.types.includes('public'),
        is_bank: this.form.types.includes('bank'),
        is_mercantile: this.form.types.includes('mercantile'),
        is_poya: this.form.types.includes('poya'),
        is_active: this.form.isActive,
        updated_by: profileId
      };

      try {
        const id = this.holidayData.id;
        const token = localStorage.getItem('token_set');
        const res = await updateHolidayData(id, payload, token);
        
        if (res.data.status === 'success') {
          toast.success(res.data.message || 'Holiday updated successfully.', { position: "top-right", timeout: 3000 });
          this.$emit('update', payload);
          this.closeModal();
        } else {
          throw new Error(res.data.message || 'Failed to update holiday.');
        }
      } catch (err) {
        console.error('Failed to update holiday:', err);
        const errorMsg = err.response?.data?.message || err.message || 'Failed to update holiday. Please try again.';
        toast.error(errorMsg, { position: "top-right", timeout: 3000 });
      } finally {
        this.isSaving = false;
      }
    }
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1050;
}
.modal-content-small {
  background-color: white;
  width: 100%;
  max-width: 450px;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(0,0,0,0.2);
}
.modal-header {
  background-color: #5A6166;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
}
.title {
  color: #fff;
  margin: 0;
  font-size: 1.1rem;
}
.close-button {
  background: none;
  border: none;
  color: white;
  font-size: 18px;
  cursor: pointer;
  padding: 0;
}
.cursor-pointer {
  cursor: pointer;
}

/* Toggle Switch Styling */
.toggle-switch {
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;
  margin: 0;
}
.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}
.slider {
  position: absolute;
  cursor: pointer;
  top: 0; left: 0; right: 0; bottom: 0;
  background-color: #ccc;
  transition: .3s;
}
.slider:before {
  position: absolute;
  content: "";
  height: 18px;
  width: 18px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  transition: .3s;
}
input:checked + .slider {
  background-color: #198754;
}
input:checked + .slider:before {
  transform: translateX(20px);
}
.slider.round {
  border-radius: 24px;
}
.slider.round:before {
  border-radius: 50%;
}
</style>
